'use client';
import React, { useEffect, useRef } from 'react';
import Image from 'next/image';

const PHONE = '9492935721';
const SMS_LINK = `sms:${PHONE}`;
const CALL_LINK = `tel:+1${PHONE}`;

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef?.current;
    if (!el) return;
    const onScroll = () => {
      const y = window.scrollY;
      el.style.transform = `translateY(${y * 0.35}px)`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section
      className="relative flex items-center justify-start overflow-hidden"
      style={{ minHeight: 'min(100vh, 700px)', paddingTop: '80px' }}
    >
      {/* Background image with parallax */}
      <div ref={heroRef} className="absolute inset-0 will-change-transform">
        <Image
          src="https://da1mambadetailing.com/assets/ChatGPT%20Image%20Jul%205,%202025%20at%2012_15_29%20PM_1751743184967.png"
          alt="Rolls Royce Ghost luxury car receiving premium mobile detailing service"
          fill
          className="object-cover object-center"
          priority
          quality={90}
        />
      </div>
      {/* Multi-layer overlay for depth */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(105deg, rgba(10,10,10,0.92) 0%, rgba(10,10,10,0.75) 50%, rgba(10,10,10,0.3) 100%)',
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to top, rgba(10,10,10,0.8) 0%, transparent 50%)',
        }}
      />
      {/* Purple atmospheric glow */}
      <div
        className="absolute"
        style={{
          bottom: '20%',
          left: '-5%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(107,33,168,0.15) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pt-8 pb-16 md:pb-20 lg:pb-28 flex items-center lg:items-end" style={{ minHeight: 'inherit' }}>
        <div className="max-w-2xl">
          {/* Label */}
          <div className="label-gold mb-6 reveal">
            <span style={{ width: '32px', height: '1px', background: 'var(--mamba-gold-light)', display: 'inline-block' }} />
            <span className="hidden sm:inline">Mobile Service · Newport Beach · Irvine · Orange County</span>
            <span className="sm:hidden">Mobile Service · Newport Beach · OC</span>
          </div>

          {/* Headline */}
          <h1
            className="section-heading reveal reveal-delay-1"
            style={{ fontSize: 'clamp(40px, 6vw, 76px)', marginBottom: '24px' }}
          >
            Your car deserves<br />
            <span className="text-gradient-purple">better than</span><br />
            a basic wash.
          </h1>

          {/* Subheadline */}
          <p
            className="reveal reveal-delay-2"
            style={{
              color: 'var(--cool-gray)',
              fontSize: 'clamp(16px, 2vw, 19px)',
              lineHeight: '1.7',
              marginBottom: '40px',
              fontFamily: 'DM Sans, sans-serif',
              maxWidth: '520px',
            }}
          >
            We come to you — mobile detailing, paint correction, and ceramic coating for Newport Beach, Irvine, and Orange County. No drop-off. No waiting. Just showroom results at your door.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-10 reveal reveal-delay-3">
            <a href={SMS_LINK} className="btn-primary" style={{ whiteSpace: 'nowrap' }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              </svg>
              Text for a Quote
            </a>
            <a href={CALL_LINK} className="btn-gold" style={{ whiteSpace: 'nowrap' }}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.01 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/>
              </svg>
              Call Now
            </a>
            <a
              href="/gallery"
              className="btn-ghost"
              style={{ whiteSpace: 'nowrap' }}
            >
              View Our Work →
            </a>
          </div>

          {/* Trust microcopy */}
          <div className="flex flex-wrap gap-x-6 gap-y-3 reveal reveal-delay-4">
            {[
              'We come to you — home or office',
              'Paint-safe process, zero shortcuts',
              'Trusted on Ferrari, Porsche & Rolls-Royce',
            ]?.map((item) => (
              <div key={item} className="flex items-center gap-2.5">
                <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--mamba-gold-light)', flexShrink: 0 }} />
                <span style={{ color: 'var(--gray-dim)', fontSize: '13px', fontFamily: 'DM Sans, sans-serif' }}>
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Bottom fade */}
      <div
        className="absolute bottom-0 inset-x-0 h-32"
        style={{ background: 'linear-gradient(to top, var(--mamba-black), transparent)' }}
      />
    </section>
  );
}
